import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/MainLayout";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Slider } from "@/components/ui/slider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Users,
  Megaphone,
  Shield,
  ShieldCheck,
  Pencil,
  Trash2,
  Plus,
  Search,
  ArrowLeft,
  ExternalLink,
  Eye,
  Clock,
  DollarSign,
  Wallet,
  CreditCard,
  FileCode,
  Crown,
  FileText,
  CheckCircle,
  XCircle,
  Calendar,
} from "lucide-react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, impersonateUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmAdminDialog, setConfirmAdminDialog] = useState(null);

  // Opportunities state
  const [adminOpps, setAdminOpps] = useState([]);
  const [oppsLoading, setOppsLoading] = useState(false);
  const [oppFormOpen, setOppFormOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [deleteConfirmOpp, setDeleteConfirmOpp] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [oppForm, setOppForm] = useState({
    name: "", description: "", category: "others", custom_type: "",
    expected_return: "", price: "", ticket: "", currency: "USD",
    url: "", image_url: "", contact: "", notes: "", risk_score: 50,
    publication_date: new Date().toISOString().split("T")[0],
    expiration_date: "",
    target_user_ids: null,
    protocol: "", chain: "", apy: "", tvl: "", token: "",
  });

  // PRO Requests state
  const [proRequests, setProRequests] = useState([]);
  const [proLoading, setProLoading] = useState(false);

  // Releases state
  const [releases, setReleases] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releaseFormOpen, setReleaseFormOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    version: "",
    title: "",
    summary: "",
    changes: [{ category: "Features", items: [""] }],
    release_date: new Date().toISOString().split("T")[0],
  });

  const resetReleaseForm = () => setReleaseForm({
    version: "",
    title: "",
    summary: "",
    changes: [{ category: "Features", items: [""] }],
    release_date: new Date().toISOString().split("T")[0],
  });

  const resetOppForm = () => setOppForm({
    name: "", description: "", category: "others", custom_type: "",
    expected_return: "", price: "", ticket: "", currency: "USD",
    url: "", image_url: "", contact: "", notes: "", risk_score: 50,
    publication_date: new Date().toISOString().split("T")[0],
    expiration_date: "",
    target_user_ids: null,
    protocol: "", chain: "", apy: "", tvl: "", token: "",
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(resp.data || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch admin opportunities
  const fetchAdminOpps = useCallback(async () => {
    setOppsLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/opportunities`, { withCredentials: true });
      setAdminOpps(resp.data || []);
    } catch (err) {
      toast.error("Failed to load opportunities");
    } finally {
      setOppsLoading(false);
    }
  }, []);

  // Fetch PRO requests
  const fetchProRequests = useCallback(async () => {
    setProLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/pro-requests`, { withCredentials: true });
      setProRequests(resp.data || []);
    } catch (err) {
      console.error("Failed to load PRO requests:", err);
    } finally {
      setProLoading(false);
    }
  }, []);

  // Fetch releases
  const fetchReleases = useCallback(async () => {
    setReleasesLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/releases`, { withCredentials: true });
      setReleases(resp.data || []);
    } catch (err) {
      console.error("Failed to load releases:", err);
    } finally {
      setReleasesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchAdminOpps();
    fetchProRequests();
    fetchReleases();
  }, [fetchUsers, fetchAdminOpps, fetchProRequests, fetchReleases]);

  // View user detail
  const viewUserDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setSelectedUser(resp.data);
    } catch (err) {
      toast.error("Failed to load user detail");
    } finally {
      setDetailLoading(false);
    }
  };

  // Toggle admin
  const handleToggleAdmin = async () => {
    if (!confirmAdminDialog) return;
    try {
      const resp = await axios.put(`${API_URL}/api/admin/users/${confirmAdminDialog.user_id}/toggle-admin`, {}, { withCredentials: true });
      toast.success(`${confirmAdminDialog.name} is ${resp.data.is_admin ? "now admin" : "no longer admin"}`);
      fetchUsers();
      if (selectedUser?.user_id === confirmAdminDialog.user_id) {
        setSelectedUser((prev) => ({ ...prev, is_admin: resp.data.is_admin }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update admin status");
    } finally {
      setConfirmAdminDialog(null);
    }
  };

  // Create/Update opportunity
  const handleSaveOpp = async () => {
    if (!oppForm.name) { toast.error("Name is required"); return; }
    try {
      const payload = { ...oppForm };
      if (!payload.expiration_date) delete payload.expiration_date;
      if (payload.target_user_ids === null) delete payload.target_user_ids;

      if (editingOpp) {
        await axios.put(`${API_URL}/api/admin/opportunities/${editingOpp.opp_id}`, payload, { withCredentials: true });
        toast.success("Opportunity updated");
      } else {
        await axios.post(`${API_URL}/api/admin/opportunities`, payload, { withCredentials: true });
        toast.success("Opportunity published");
      }
      setOppFormOpen(false);
      setEditingOpp(null);
      resetOppForm();
      fetchAdminOpps();
    } catch (err) {
      toast.error("Failed to save opportunity");
    }
  };

  const handleEditOpp = (opp) => {
    setEditingOpp(opp);
    setOppForm({
      name: opp.name || "", description: opp.description || "",
      category: opp.category || "others", custom_type: opp.custom_type || "",
      expected_return: opp.expected_return || "", price: opp.price || "",
      ticket: opp.ticket || "", currency: opp.currency || "USD",
      url: opp.url || "", image_url: opp.image_url || "",
      contact: opp.contact || "", notes: opp.notes || "",
      risk_score: opp.risk_score || 50,
      publication_date: opp.publication_date || "",
      expiration_date: opp.expiration_date || "",
      target_user_ids: opp.target_user_ids || null,
      protocol: opp.protocol || "", chain: opp.chain || "",
      apy: opp.apy || "", tvl: opp.tvl || "", token: opp.token || "",
    });
    setOppFormOpen(true);
  };

  const handleDeleteOpp = async () => {
    if (!deleteConfirmOpp) return;
    try {
      await axios.delete(`${API_URL}/api/admin/opportunities/${deleteConfirmOpp.opp_id}`, { withCredentials: true });
      toast.success("Opportunity deleted");
      fetchAdminOpps();
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setDeleteConfirmOpp(null);
    }
  };

  const handleGenerateReport = async (userId) => {
    setGeneratingReport(true);
    try {
      await axios.post(`${API_URL}/api/admin/reports/generate/${userId}`, {}, { withCredentials: true });
      toast.success("Report generated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      await impersonateUser(userId);
      toast.success("Now viewing as user");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
  };

  const getTimeAgo = (iso) => {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const d = Math.floor(diff / 86400000);
    if (d > 30) return `${Math.floor(d / 30)}mo ago`;
    if (d > 0) return `${d}d ago`;
    const h = Math.floor(diff / 3600000);
    if (h > 0) return `${h}h ago`;
    return "Just now";
  };

  const filteredUsers = users.filter((u) => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const getRiskBadge = (score) => {
    if (score <= 33) return <Badge className="bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/10 text-[10px]">Low</Badge>;
    if (score <= 66) return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/10 text-[10px]">Med</Badge>;
    return <Badge className="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/10 text-[10px]">High</Badge>;
  };

  // PRO Request handlers
  const handleUpdateProRequest = async (requestId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/pro-requests/${requestId}`, { status }, { withCredentials: true });
      toast.success(`Request ${status}`);
      fetchProRequests();
    } catch (err) {
      toast.error("Failed to update request");
    }
  };

  // Release handlers
  const handleSaveRelease = async () => {
    try {
      if (editingRelease) {
        await axios.put(`${API_URL}/api/admin/releases/${editingRelease.release_id}`, releaseForm, { withCredentials: true });
        toast.success("Release updated");
      } else {
        await axios.post(`${API_URL}/api/admin/releases`, releaseForm, { withCredentials: true });
        toast.success("Release created");
      }
      setReleaseFormOpen(false);
      resetReleaseForm();
      setEditingRelease(null);
      fetchReleases();
    } catch (err) {
      toast.error("Failed to save release");
    }
  };

  const handleDeleteRelease = async (releaseId) => {
    if (!window.confirm("Delete this release note?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/releases/${releaseId}`, { withCredentials: true });
      toast.success("Release deleted");
      fetchReleases();
    } catch (err) {
      toast.error("Failed to delete release");
    }
  };

  const openEditRelease = (release) => {
    setEditingRelease(release);
    setReleaseForm({
      version: release.version || "",
      title: release.title || "",
      summary: release.summary || "",
      changes: release.changes?.length > 0 ? release.changes : [{ category: "Features", items: [""] }],
      release_date: release.release_date || new Date().toISOString().split("T")[0],
    });
    setReleaseFormOpen(true);
  };

  const addChangeCategory = () => {
    setReleaseForm(prev => ({
      ...prev,
      changes: [...prev.changes, { category: "", items: [""] }]
    }));
  };

  const updateChangeCategory = (idx, category) => {
    setReleaseForm(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => i === idx ? { ...c, category } : c)
    }));
  };

  const addChangeItem = (catIdx) => {
    setReleaseForm(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => i === catIdx ? { ...c, items: [...c.items, ""] } : c)
    }));
  };

  const updateChangeItem = (catIdx, itemIdx, value) => {
    setReleaseForm(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => i === catIdx ? {
        ...c,
        items: c.items.map((item, j) => j === itemIdx ? value : item)
      } : c)
    }));
  };

  if (!user?.is_admin) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md w-full">
            <CardContent className="py-12 text-center">
              <Shield className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">{t("admin.accessDenied")}</h2>
              <p className="text-muted-foreground">{t("admin.noPrivileges")}</p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-panel">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider">{t("admin.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("admin.subtitle")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/architecture")} className="gap-2" data-testid="architecture-link">
            <FileCode className="w-4 h-4" /> Architecture Docs
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="users" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" /> {t("admin.users")} ({users.length})
            </TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="admin-tab-opportunities">
              <Megaphone className="w-4 h-4 mr-2" /> {t("admin.pushedOpps")} ({adminOpps.length})
            </TabsTrigger>
            <TabsTrigger value="pro-requests" data-testid="admin-tab-pro-requests">
              <Crown className="w-4 h-4 mr-2" /> PRO Requests ({proRequests.filter(r => r.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="releases" data-testid="admin-tab-releases">
              <FileText className="w-4 h-4 mr-2" /> Releases ({releases.length})
            </TabsTrigger>
          </TabsList>

          {/* ===== USERS TAB ===== */}
          <TabsContent value="users" className="space-y-4">
            {selectedUser ? (
              /* User Detail View */
              <div className="space-y-4" data-testid="user-detail-view">
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to list
                </Button>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={selectedUser.picture} />
                        <AvatarFallback className="text-xl">{selectedUser.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-bold">{selectedUser.name}</h2>
                          {selectedUser.is_admin && <Badge className="bg-primary/10 text-primary text-[10px]">Admin</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Last login: {getTimeAgo(selectedUser.last_login)} | Joined: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : "N/A"}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImpersonate(selectedUser.user_id)}
                        data-testid="detail-impersonate-btn"
                        className="gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Connect As
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmAdminDialog(selectedUser)}
                        disabled={selectedUser.email === "lucfalempin@gmail.com"}
                        data-testid="toggle-admin-btn"
                      >
                        {selectedUser.is_admin ? <Shield className="w-4 h-4 mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                        {selectedUser.is_admin ? "Remove Admin" : "Make Admin"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateReport(selectedUser.user_id)}
                        disabled={generatingReport}
                        data-testid="admin-generate-report-btn"
                        className="gap-1"
                      >
                        {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                        {generatingReport ? "Generating..." : "Generate Report"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {/* Portfolio Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Portfolio Value", value: formatCurrency(selectedUser.portfolio_summary?.total_value), icon: Wallet, color: "text-primary" },
                    { label: "P/L", value: formatCurrency(selectedUser.portfolio_summary?.gain_loss), icon: DollarSign, color: (selectedUser.portfolio_summary?.gain_loss || 0) >= 0 ? "text-green-500" : "text-red-500" },
                    { label: "Assets", value: selectedUser.portfolio_summary?.asset_count, icon: Wallet, color: "text-blue-500" },
                    { label: "Credits", value: selectedUser.portfolio_summary?.credit_count, icon: CreditCard, color: "text-orange-500" },
                  ].map((stat) => (
                    <Card key={stat.label}>
                      <CardContent className="pt-4 pb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <stat.icon className={`w-4 h-4 ${stat.color}`} />
                          <p className="text-xs text-muted-foreground">{stat.label}</p>
                        </div>
                        <p className={`text-xl font-mono font-bold ${stat.color}`}>{stat.value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {/* Assets List */}
                {selectedUser.assets?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">Assets</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead className="text-center">Risk</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedUser.assets.map((a, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{a.name} <span className="text-[10px] text-muted-foreground font-mono ml-1">{a.currency || "USD"}</span></TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{a.asset_type}</Badge></TableCell>
                              <TableCell className="text-right font-mono">{a.quantity}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency((a.current_price || a.purchase_price) * a.quantity)}</TableCell>
                              <TableCell className="text-center">{getRiskBadge(a.risk_score)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              /* Users List */
              <div className="space-y-4" data-testid="users-list-view">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder={t("admin.searchUsers")} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9" data-testid="user-search-input" />
                  </div>
                </div>
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Portfolio</TableHead>
                          <TableHead className="text-center">Assets</TableHead>
                          <TableHead>Last Login</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((u) => (
                          <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="cursor-pointer hover:bg-accent/50" onClick={() => viewUserDetail(u.user_id)}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={u.picture} />
                                  <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{u.name}</p>
                                  <p className="text-xs text-muted-foreground">{u.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {u.is_admin ? (
                                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 text-[10px]">Admin</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">User</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">{formatCurrency(u.total_portfolio_value)}</TableCell>
                            <TableCell className="text-center font-mono">{u.asset_count}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" /> {getTimeAgo(u.last_login)}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleImpersonate(u.user_id); }}
                                  title="Connect as this user"
                                  data-testid={`impersonate-btn-${u.user_id}`}
                                >
                                  <Eye className="w-4 h-4 text-muted-foreground" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); setConfirmAdminDialog(u); }}
                                  disabled={u.email === "lucfalempin@gmail.com"}
                                >
                                  {u.is_admin ? <Shield className="w-4 h-4 text-primary" /> : <ShieldCheck className="w-4 h-4 text-muted-foreground" />}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredUsers.length === 0 && (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ===== OPPORTUNITIES TAB ===== */}
          <TabsContent value="opportunities" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{adminOpps.length} published opportunit{adminOpps.length !== 1 ? "ies" : "y"}</p>
              <Button
                className="bg-primary text-primary-foreground font-bold uppercase tracking-wider text-xs"
                onClick={() => { resetOppForm(); setEditingOpp(null); setOppFormOpen(true); }}
                data-testid="create-admin-opp-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Opportunity
              </Button>
            </div>
            {adminOpps.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Published</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-center">Risk</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminOpps.map((opp) => (
                        <TableRow key={opp.opp_id} data-testid={`admin-opp-row-${opp.opp_id}`}>
                          <TableCell>
                            <p className="font-medium text-sm">{opp.name}</p>
                            {opp.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{opp.description}</p>}
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{opp.category}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{opp.target_user_ids ? `${opp.target_user_ids.length} users` : "All users"}</TableCell>
                          <TableCell className="text-xs font-mono">{opp.publication_date}</TableCell>
                          <TableCell className="text-xs font-mono">{opp.expiration_date || "Never"}</TableCell>
                          <TableCell className="text-center">{getRiskBadge(opp.risk_score)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditOpp(opp)} data-testid={`edit-opp-${opp.opp_id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmOpp(opp)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-1">No published opportunities yet</p>
                  <p className="text-sm text-muted-foreground">Push your first recommendation to all users</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== PRO REQUESTS TAB ===== */}
          <TabsContent value="pro-requests" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" /> PRO Upgrade Requests
                </CardTitle>
                <CardDescription>Manage user upgrade requests</CardDescription>
              </CardHeader>
              <CardContent>
                {proLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : proRequests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No PRO upgrade requests</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Requested Tier</TableHead>
                        <TableHead>Source Page</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proRequests.map((req) => (
                        <TableRow key={req.request_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{req.user_name || "Unknown"}</p>
                              <p className="text-xs text-muted-foreground">{req.email || req.user_id}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                              {req.requested_tier?.toUpperCase() || "PRO"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{req.source_page || "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : "—"}
                          </TableCell>
                          <TableCell>
                            {req.status === "pending" && <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Pending</Badge>}
                            {req.status === "approved" && <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Approved</Badge>}
                            {req.status === "rejected" && <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Rejected</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            {req.status === "pending" && (
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => handleUpdateProRequest(req.request_id, "approved")} className="text-green-500 hover:text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleUpdateProRequest(req.request_id, "rejected")} className="text-red-500 hover:text-red-600">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== RELEASES TAB ===== */}
          <TabsContent value="releases" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Release Notes</h2>
                <p className="text-sm text-muted-foreground">Document changes for each version</p>
              </div>
              <Button onClick={() => { resetReleaseForm(); setEditingRelease(null); setReleaseFormOpen(true); }} data-testid="create-release-btn">
                <Plus className="w-4 h-4 mr-2" /> New Release
              </Button>
            </div>

            {releasesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : releases.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No releases documented yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {releases.map((rel) => (
                  <Card key={rel.release_id} data-testid={`release-${rel.release_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono text-primary border-primary/30">v{rel.version}</Badge>
                          <CardTitle className="text-lg">{rel.title}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {rel.release_date}
                          </span>
                          <Button size="sm" variant="ghost" onClick={() => openEditRelease(rel)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteRelease(rel.release_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {rel.summary && <CardDescription>{rel.summary}</CardDescription>}
                    </CardHeader>
                    <CardContent>
                      {rel.changes?.length > 0 && (
                        <div className="space-y-3">
                          {rel.changes.map((change, idx) => (
                            <div key={idx}>
                              <h4 className="text-sm font-semibold text-primary mb-1">{change.category}</h4>
                              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                                {change.items?.filter(i => i).map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Release Form Dialog */}
            <Dialog open={releaseFormOpen} onOpenChange={(open) => { if (!open) { setReleaseFormOpen(false); setEditingRelease(null); } }}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRelease ? "Edit Release" : "Create Release"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Version *</Label>
                      <Input placeholder="1.0.0" value={releaseForm.version} onChange={(e) => setReleaseForm({ ...releaseForm, version: e.target.value })} data-testid="release-version" />
                    </div>
                    <div className="col-span-2">
                      <Label>Title *</Label>
                      <Input placeholder="March 2026 Release" value={releaseForm.title} onChange={(e) => setReleaseForm({ ...releaseForm, title: e.target.value })} data-testid="release-title" />
                    </div>
                  </div>
                  <div>
                    <Label>Release Date</Label>
                    <Input type="date" value={releaseForm.release_date} onChange={(e) => setReleaseForm({ ...releaseForm, release_date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Summary</Label>
                    <Textarea placeholder="Brief overview of this release..." value={releaseForm.summary} onChange={(e) => setReleaseForm({ ...releaseForm, summary: e.target.value })} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Changes</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addChangeCategory}>
                        <Plus className="w-3 h-3 mr-1" /> Add Category
                      </Button>
                    </div>
                    {releaseForm.changes.map((cat, catIdx) => (
                      <div key={catIdx} className="mb-4 p-3 border rounded-md">
                        <Input placeholder="Category (e.g., Features, Bug Fixes, Improvements)" value={cat.category} onChange={(e) => updateChangeCategory(catIdx, e.target.value)} className="mb-2 font-semibold" />
                        {cat.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex gap-2 mb-1">
                            <Input placeholder="Change description..." value={item} onChange={(e) => updateChangeItem(catIdx, itemIdx, e.target.value)} className="text-sm" />
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" onClick={() => addChangeItem(catIdx)} className="text-xs">
                          <Plus className="w-3 h-3 mr-1" /> Add item
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setReleaseFormOpen(false); setEditingRelease(null); }}>Cancel</Button>
                  <Button onClick={handleSaveRelease} data-testid="save-release-btn">
                    {editingRelease ? "Update" : "Create"} Release
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* ===== Admin Toggle Confirmation Dialog ===== */}
        <Dialog open={!!confirmAdminDialog} onOpenChange={(open) => !open && setConfirmAdminDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Admin Status Change</DialogTitle>
              <DialogDescription>
                {confirmAdminDialog?.is_admin
                  ? `Remove admin privileges from ${confirmAdminDialog?.name}?`
                  : `Grant admin privileges to ${confirmAdminDialog?.name} (${confirmAdminDialog?.email})?`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmAdminDialog(null)}>Cancel</Button>
              <Button onClick={handleToggleAdmin} className="bg-primary text-primary-foreground" data-testid="confirm-admin-toggle-btn">
                {confirmAdminDialog?.is_admin ? "Remove Admin" : "Make Admin"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Delete Opportunity Confirmation ===== */}
        <Dialog open={!!deleteConfirmOpp} onOpenChange={(open) => !open && setDeleteConfirmOpp(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Opportunity</DialogTitle>
              <DialogDescription>
                Delete "{deleteConfirmOpp?.name}"? This will remove it from all users.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpp(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteOpp} data-testid="confirm-delete-opp-btn">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Create/Edit Opportunity Dialog ===== */}
        <Dialog open={oppFormOpen} onOpenChange={(open) => { if (!open) { setOppFormOpen(false); setEditingOpp(null); } }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">{editingOpp ? "Edit" : "Create"} Opportunity</DialogTitle>
              <DialogDescription>This will be pushed as a "Cableway Recommended" opportunity</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Category selector */}
                <div className="col-span-2">
                  <Label>Category</Label>
                  <Select value={oppForm.category} onValueChange={(v) => setOppForm({ ...oppForm, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="defi">DeFi</SelectItem>
                      <SelectItem value="web3_secondaries">Web3 Secondaries</SelectItem>
                      <SelectItem value="stocks">Stocks</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input data-testid="admin-opp-name" value={oppForm.name} onChange={(e) => setOppForm({ ...oppForm, name: e.target.value })} placeholder={oppForm.category === "defi" ? "e.g., Aave USDC Lending" : "e.g., Startup Alpha"} />
                </div>

                {/* DeFi-specific fields */}
                {oppForm.category === "defi" && (
                  <>
                    <div>
                      <Label>Protocol</Label>
                      <Input placeholder="Aave, Compound" value={oppForm.protocol} onChange={(e) => setOppForm({ ...oppForm, protocol: e.target.value })} />
                    </div>
                    <div>
                      <Label>Chain</Label>
                      <Input placeholder="Ethereum, Polygon" value={oppForm.chain} onChange={(e) => setOppForm({ ...oppForm, chain: e.target.value })} />
                    </div>
                    <div>
                      <Label>APY (%)</Label>
                      <Input type="number" step="0.01" placeholder="5.5" value={oppForm.apy} onChange={(e) => setOppForm({ ...oppForm, apy: e.target.value })} />
                    </div>
                    <div>
                      <Label>TVL (USD)</Label>
                      <Input type="number" placeholder="1000000" value={oppForm.tvl} onChange={(e) => setOppForm({ ...oppForm, tvl: e.target.value })} />
                    </div>
                    <div>
                      <Label>Token</Label>
                      <Input placeholder="USDC, ETH" value={oppForm.token} onChange={(e) => setOppForm({ ...oppForm, token: e.target.value.toUpperCase() })} />
                    </div>
                  </>
                )}

                {/* Non-DeFi fields */}
                {oppForm.category !== "defi" && (
                  <>
                    <div className="col-span-2">
                      <Label>Description</Label>
                      <Input value={oppForm.description} onChange={(e) => setOppForm({ ...oppForm, description: e.target.value })} placeholder="Brief description..." />
                    </div>
                    <div>
                      <Label>Type (free text)</Label>
                      <Input value={oppForm.custom_type} onChange={(e) => setOppForm({ ...oppForm, custom_type: e.target.value })} placeholder="e.g., SAFT, ETF" />
                    </div>
                    <div>
                      <Label>Expected Return</Label>
                      <Input value={oppForm.expected_return} onChange={(e) => setOppForm({ ...oppForm, expected_return: e.target.value })} placeholder="e.g., 15% p.a." />
                    </div>
                    <div>
                      <Label>Price</Label>
                      <Input value={oppForm.price} onChange={(e) => setOppForm({ ...oppForm, price: e.target.value })} placeholder="$150" />
                    </div>
                    <div>
                      <Label>Ticket Min</Label>
                      <Input value={oppForm.ticket} onChange={(e) => setOppForm({ ...oppForm, ticket: e.target.value })} placeholder="$10,000" />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={oppForm.currency} onValueChange={(v) => setOppForm({ ...oppForm, currency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                          <SelectItem value="BTC">BTC</SelectItem>
                          <SelectItem value="ETH">ETH</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Contact</Label>
                      <Input value={oppForm.contact} onChange={(e) => setOppForm({ ...oppForm, contact: e.target.value })} placeholder="email or link" />
                    </div>
                    <div>
                      <Label>Image URL</Label>
                      <Input value={oppForm.image_url} onChange={(e) => setOppForm({ ...oppForm, image_url: e.target.value })} placeholder="https://..." />
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Input value={oppForm.notes} onChange={(e) => setOppForm({ ...oppForm, notes: e.target.value })} placeholder="Additional notes..." />
                    </div>
                  </>
                )}

                {/* Common fields */}
                <div>
                  <Label>URL (optional)</Label>
                  <Input value={oppForm.url} onChange={(e) => setOppForm({ ...oppForm, url: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <Label>Publication Date</Label>
                  <Input type="date" value={oppForm.publication_date} onChange={(e) => setOppForm({ ...oppForm, publication_date: e.target.value })} data-testid="admin-opp-pub-date" />
                </div>
                <div>
                  <Label>Expiration Date</Label>
                  <Input type="date" value={oppForm.expiration_date} onChange={(e) => setOppForm({ ...oppForm, expiration_date: e.target.value })} data-testid="admin-opp-exp-date" />
                </div>
                <div>
                  <Label>Risk Score: {oppForm.risk_score}</Label>
                  <Slider value={[oppForm.risk_score]} onValueChange={([v]) => setOppForm({ ...oppForm, risk_score: v })} max={100} step={1} className="mt-2" />
                </div>
                <div className="col-span-2">
                  <Label>Target</Label>
                  <Select value={oppForm.target_user_ids === null ? "all" : "selected"} onValueChange={(v) => setOppForm({ ...oppForm, target_user_ids: v === "all" ? null : [] })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="selected">Selected Users</SelectItem>
                    </SelectContent>
                  </Select>
                  {oppForm.target_user_ids !== null && (
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto border rounded-md p-2">
                      {users.map((u) => (
                        <label key={u.user_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent p-1 rounded">
                          <input
                            type="checkbox"
                            checked={oppForm.target_user_ids?.includes(u.user_id)}
                            onChange={(e) => {
                              const ids = oppForm.target_user_ids || [];
                              setOppForm({
                                ...oppForm,
                                target_user_ids: e.target.checked
                                  ? [...ids, u.user_id]
                                  : ids.filter((id) => id !== u.user_id),
                              });
                            }}
                          />
                          {u.name} <span className="text-xs text-muted-foreground">({u.email})</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOppFormOpen(false); setEditingOpp(null); }}>Cancel</Button>
              <Button onClick={handleSaveOpp} className="bg-primary text-primary-foreground" data-testid="save-admin-opp-btn">
                {editingOpp ? "Update" : "Publish"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default AdminPage;
